<template>
  <v-menu
    v-model="open"
    :close-on-content-click="false"
    :nudge-bottom="41"
    eager
    @input="reset"
  >
    <template #activator="{ on }">
      <slot name="activator" :on="on" />
    </template>
    <v-card class="sv-card">
      <div class="dt-range-picker">
        <div class="dt-range-picker__body">
          <div v-if="isShowRanges" class="dt-range-picker__ranges-list">
            <calendar-ranges
              v-model="selectedRange"
              :locale="locale"
              :max-range="maxRange"
              :ranges="ranges"
              @input="onChangeRange"
            />
          </div>

          <div class="dt-range-picker__first-date">
            <calendar
              :in-selection="in_selection"
              :month-date="monthDate"
              :locale-data="locale"
              :start="start"
              :end="end"
              :max-range="maxRange"
              @change-month="changeLeftMonth"
              @dateClick="dateClick"
              @hoverDate="hoverDate"
            />
            <calendar-time
              v-if="timePicker"
              :miniute-increment="timePickerIncrement"
              :second-picker="timePickerSeconds"
              :current-time="startTime"
              @update="onUpdateStartTime"
            />
          </div>
          <div v-if="!singleDatePicker" class="dt-range-picker__second-date">
            <calendar
              :in-selection="in_selection"
              :month-date="nextMonthDate"
              :locale-data="locale"
              :start="start"
              :end="end"
              :max-range="maxRange"
              @change-month="changeRightMonth"
              @dateClick="dateClick"
              @hoverDate="hoverDate"
            />
            <calendar-time
              v-if="timePicker"
              :miniute-increment="timePickerIncrement"
              :second-picker="timePickerSeconds"
              :current-time="endTime"
              @update="onUpdateEndTime"
            />
          </div>
        </div>

        <div class="dt-range-picker__footer">
          <v-btn
            color="primary"
            small
            depressed
            outlined
            rounded
            @click="onClickCancel"
          >
            {{ locale.cancelLabel }}
          </v-btn>
          <v-btn
            color="primary"
            small
            depressed
            rounded
            :disabled="in_selection || !start || !end"
            @click="onClickApply"
          >
            {{ locale.applyLabel }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { size } from "lodash";
import { DateTime } from "luxon";
import Calendar from "./Calendar.vue";
import CalendarTime from "./CalendarTime";
import CalendarRanges from "./CalendarRanges";
import { localeData, DefaultRanges } from "./util";

export default {
  components: { Calendar, CalendarTime, CalendarRanges },
  inheritAttrs: false,
  model: {
    prop: "dateRange",
    event: "update"
  },
  props: {
    /**
     * Each calendar has separate navigation when this is false
     */
    defaultOpen: {
      type: Boolean,
      default: false
    },

    linkedCalendars: {
      type: Boolean,
      default: true
    },
    /**
     * Allows you to select only one date (instead of range). This will hide the ranges with different start/end
     */
    singleDatePicker: {
      type: Boolean,
      default: false
    },
    /**
     * Show the dropdowns for time (hour/minute) selection below the calendars
     */
    timePicker: {
      type: Boolean,
      default: false
    },

    range: {
      type: String,
      default: null
    },
    /**
     * Determines the increment of minutes in the minute dropdown
     */
    timePickerIncrement: {
      type: Number,
      default: 1
    },
    /**
     * Allows you to select seconds except hour/minute
     */
    timePickerSeconds: {
      type: Boolean,
      default: false
    },
    /**
     * Object containing locale data used by the picker. See example below the table
     *
     * @default *see below
     */
    localeData: {
      type: Object,
      default() {
        return {};
      }
    },
    dateRange: {
      type: Object,
      required: true
    },
    maxRange: {
      type: Number,
      default: 0
    },
    ranges: {
      type: [Object, Boolean],
      default: () => DefaultRanges
    }
  },
  data() {
    let locale = localeData(this.localeData, this.$i18n);
    return {
      in_selection: false,
      cachedRange: null,
      open: this.defaultOpen,
      start: null,
      end: null,
      endTime: DateTime.local().startOf("day"),
      startTime: DateTime.local().startOf("day"),
      monthDate: DateTime.local(),
      nextMonthDate: DateTime.local(),
      locale
    };
  },
  computed: {
    selectedRange: {
      get() {
        return this.range;
      },
      set(v) {
        this.$emit("update:range", v);
      }
    },
    isEmptyRange() {
      return !this.dateRange.startDate || !this.dateRange.endDate;
    },
    isShowRanges() {
      if (!this.ranges) return false;
      return size(this.ranges) > 0;
    },
    startDateTime() {
      const { start, startTime } = this;
      if (!start) return null;
      const { hour, second, minute } = startTime;
      return start.set({ hour, second, minute });
    },
    endDateTime() {
      const { end, endTime } = this;
      if (!end) return null;
      const { hour, second, minute } = endTime;
      return end.set({ hour, second, minute });
    }
  },
  watch: {
    "dateRange.startDate"(value) {
      this.setStartDateTime(
        !!value && !this.isEmptyRange ? DateTime.fromISO(value) : null
      );
      this.setRange();
    },
    "dateRange.endDate"(value) {
      this.setEndDateTime(
        !!value && !this.isEmptyRange ? DateTime.fromISO(value) : null
      );
      this.setRange();
    },
    open(v) {
      if (v) this.cachedRange = this.selectedRange;
    }
  },
  created() {
    this.setRange();
  },
  methods: {
    changeLeftMonth(dt) {
      this.monthDate = dt;
    },
    changeRightMonth(dt) {
      this.nextMonthDate = dt;
    },
    dateClick(date) {
      this.selectedRange = null;
      if (this.in_selection) {
        this.in_selection = false;
        this.end = date;
        if (this.end < this.start) {
          this.in_selection = true;
          this.start = date;
        }
      } else {
        this.start = date;
        this.end = date;
        this.in_selection = true;
      }
    },
    hoverDate(date) {
      if (this.in_selection && date >= this.start) this.end = date;
    },
    onClickApply() {
      const { startDateTime, endDateTime } = this;
      this.$emit("update", { startDate: startDateTime, endDate: endDateTime });
      this.open = false;
    },
    onClickCancel() {
      this.open = false;
      this.reset();
    },
    reset(v) {
      if (v) return;
      const { startDate, endDate } = this.dateRange;
      this.start = startDate ? DateTime.fromISO(startDate) : null;
      this.end = endDate ? DateTime.fromISO(endDate) : null;
      this.startTime = startDate
        ? DateTime.fromISO(startDate)
        : DateTime.local().startOf("day");
      this.endTime = endDate
        ? DateTime.fromISO(endDate)
        : DateTime.local().startOf("day");
      this.selectedRange = this.cachedRange;
    },
    setRange() {
      if (this.isEmptyRange) {
        this.start = null;
        this.end = null;
        this.endTime = DateTime.local().startOf("day");
        this.startTime = DateTime.local().startOf("day");
      } else {
        const start = DateTime.fromISO(this.dateRange.startDate);
        const end = DateTime.fromISO(this.dateRange.endDate);
        this.setStartDateTime(start);
        this.setEndDateTime(end);
        this.monthDate = start;
        this.nextMonthDate = start.hasSame(end, "month")
          ? end.plus({ months: 1 })
          : end;
      }
    },
    setEndDateTime(value) {
      this.end = value;
      if (value) this.endTime = value;
    },
    setStartDateTime(value) {
      this.start = value;
      if (value) this.startTime = value;
    },
    onUpdateStartTime({ hour, minute, second }) {
      const { startTime } = this;
      this.selectedRange = null;
      this.startTime = startTime.set({ minute, hour, second });
    },
    onUpdateEndTime({ minute, hour, second }) {
      const { endTime } = this;
      this.selectedRange = null;
      this.endTime = endTime.set({ minute, hour, second });
    },
    onChangeRange(rangeKey) {
      const { start, end } = rangeKey ? this.ranges[rangeKey]() : {};
      this.start = start;
      this.end = end;
      if (start) {
        this.startTime = start;
      }
      if (end) {
        this.endTime = end;
      }
      this.in_selection = false;
      this.monthDate = start || DateTime.local();
      this.nextMonthDate = end
        ? end.plus({ months: 1 })
        : DateTime.local().plus({ months: 1 });
    }
  }
};
</script>
<style lang="scss">
@import "./assets/index.scss";
</style>
